/**
 * The quality tier a headset session runs at, and the governor that defends it.
 *
 * Nothing in this file runs unless an immersive session is presenting. On
 * desktop it is a module that is never entered: no timers, no listeners, no
 * per-frame cost, and `TUNING.render` is left exactly as it was authored.
 *
 * ## Why a tier is needed at all
 *
 * A headset is not "the same game on a smaller screen". Two things change at
 * once and they pull in opposite directions:
 *
 * **Draw calls double.** three's WebGL renderer has no multiview. In
 * `WebGLRenderer.render`, an `ArrayCamera` is drawn by looping the *whole*
 * render list once per eye (`renderScene(list, scene, camera2, viewport)`), so
 * every mesh is submitted twice. Level 94 sits at ~409 draw calls at its p95 on
 * desktop; in stereo that is ~818 submissions per frame, and at 72 Hz ~59,000
 * per second on a mobile driver. That number has to come down before anything
 * else matters.
 *
 * **Pixels triple to quintuple.** At framebuffer scale 1.0 a Quest 3 hands out
 * roughly 2064x2208 per eye — about 9.1 Mpx a frame, 656 Mpx/s at 72 Hz,
 * against ~124 Mpx/s for a 1080p desktop view at 60. That is 5x the fill on a
 * GPU with a small fraction of a desktop card's bandwidth. At the 0.8 scale
 * this tier asks for, with foveation on, it lands near the 2.5x the rest of the
 * work assumes.
 *
 * ## Where the cuts come from
 *
 * - **Cull distance 68 -> 44 m**, and `viewDistance` capped at 64 m so Level 94
 *   (which asks for 118) and Level 11 (92) keep some of their skyline. Visible
 *   area goes as the square: 44/68 is 42% of the desktop area, 64/118 is 29% of
 *   Level 94's. Scenery falls with it, because `Renderer.cullProps` reads the
 *   same `TUNING.render.cullDistance` — and scenery is what makes Level 94 the
 *   worst case in the first place. Estimated ~409 -> ~150 draw calls, ~300 in
 *   stereo, which is inside what a headset browser can push.
 * - **Dynamic lights 12 -> 6.** `render/lights.ts` keeps every pool slot in the
 *   scene at zero intensity, because removing one recompiles every material.
 *   So a forward fragment shader pays for all twelve on every pixel of every
 *   surface, lit or not. Halving the pool halves that loop. This is the largest
 *   per-pixel saving available and it costs the least visually, because six is
 *   what the shipped `low` quality tier already uses.
 * - **The fullscreen post chain is gone**, replaced by a blend-only overlay.
 *   See `render/postfx.ts` for what survives and what it costs.
 * - **Framebuffer scale 0.8**, set before `requestSession` because three cannot
 *   change it afterwards, plus fixed foveation the governor can move live.
 *
 * ## The governor
 *
 * Everything above is a guess made without a headset. The governor is the part
 * that does not have to be right: it watches the frame interval, and because an
 * XR frame is vsync-locked, a late frame is a *dropped* frame — the interval
 * jumps to twice the refresh, which is unambiguous. When drops become a pattern
 * it steps quality down; when a session has been clean for a while it gives one
 * step back and then stops trying, so it cannot oscillate.
 *
 * It only pulls levers that are safe to move mid-session:
 * foveation, then the post overlay, then the session's target frame rate.
 * Draw distance is deliberately *not* one of them — shortening it live would
 * pop scenery inside the fog the player can still see through, and a pop in a
 * headset happens in both eyes at head speed.
 *
 * ## Unverified
 *
 * Every number here is derived from the repository and from published device
 * figures. None of it has been run on a Quest 3 or a Vision Pro. The governor
 * exists precisely because of that.
 */

import type { WebGLRenderer } from 'three';
import { TUNING } from '../core/config';
import { clamp, clamp01 } from '../core/math';
import type { RenderSettings } from '../core/types';

/* ------------------------------------------------------------------ *
 * State read by the render path
 * ------------------------------------------------------------------ */

/** Live grade switch. The governor's last quality lever before frame rate. */
let gradeOn = true;
/** 0..1 locomotion input for the comfort vignette. Fed from outside; see below. */
let comfortMotion = 0;

/**
 * Whether the in-session post overlay should draw this frame.
 *
 * `render/postfx.ts` reads this rather than being told, because the post chain
 * is private to the renderer and the governor has no handle on it. False on
 * desktop is meaningless — the desktop chain never asks.
 */
export function xrPostGrade(): boolean {
  return gradeOn;
}

/**
 * Strength of the comfort vignette, 0..1.
 *
 * The baseline is always on; the rest is proportional to how fast the player is
 * moving under their own control, which is the thing that actually causes sim
 * sickness. Nothing feeds the motion term yet — see `setXRComfortMotion`.
 */
export function xrComfort(): number {
  const t = TUNING.render.xr;
  return clamp01(t.comfortVignette + t.comfortVignetteMoving * comfortMotion);
}

/**
 * Report how fast the player is moving, 0..1, where 1 is full walking speed
 * under stick or key control. Called from wherever XR locomotion lives; safe to
 * never call, in which case only the baseline vignette applies.
 *
 * Teleport locomotion does not need this. Smooth locomotion does: peripheral
 * optic flow while the inner ear reports stillness is the mechanism behind
 * almost all VR nausea, and darkening the periphery during motion is the
 * cheapest mitigation there is.
 */
export function setXRComfortMotion(v: number): void {
  comfortMotion = clamp01(v);
}

/* ------------------------------------------------------------------ *
 * The tier
 * ------------------------------------------------------------------ */

/** What a session overrides in `TUNING.render`, so it can be put back exactly. */
interface TuningSnapshot {
  cullDistance: number;
  viewDistanceCap: number;
  fogFarScale: number;
  maxDynamicLights: number;
}

/** A rung on the quality ladder. Lower index is better looking. */
interface Rung {
  /** Fixed foveation, 0 = full resolution everywhere. */
  foveation: number;
  /** Whether the post overlay draws. */
  grade: boolean;
}

/**
 * Build the ladder from where the session already is.
 *
 * `base` is the foveation the session started with, and no rung is ever below
 * it: a governor that *lowers* foveation is a governor that makes the frame
 * more expensive, which is the opposite of its job. three's own default is 1
 * and `render/xr.ts` asks for 1 as well, so in practice there is no headroom
 * here at all — the duplicate rungs collapse and the ladder comes out two
 * rungs long: with the grade, and without it. That is the honest state of
 * things, not an oversight: everything else worth cutting has to be cut before
 * the session starts, because cutting it later either pops or recompiles.
 */
function ladder(base: number): readonly Rung[] {
  const floor = Math.max(clamp01(base), clamp01(TUNING.render.xr.foveation));
  const climb = [0, 0.34, 0.67, 1].map((f) => ({ foveation: floor + (1 - floor) * f, grade: true }));
  const rungs: Rung[] = [];
  for (const rung of climb) {
    // A rung that changes nothing is a window and a half of dropped frames
    // spent doing nothing about them.
    if (rungs.length > 0 && rung.foveation - rungs[rungs.length - 1]!.foveation < 0.02) continue;
    rungs.push(rung);
  }
  // Last rung: the grade goes. It is the only fullscreen fill the session adds
  // on top of the scene, so it is the only thing left to give.
  rungs.push({ foveation: rungs[rungs.length - 1]!.foveation, grade: false });
  return rungs;
}

/** What the governor learned, for a debug readout or to carry to next session. */
export interface XRQualityStats {
  /** Rung index, 0 = the tier as authored. */
  readonly level: number;
  /** Frames seen this session. */
  readonly frames: number;
  /** Frames that arrived at least `governorLateFactor` refreshes late. */
  readonly dropped: number;
  /** Refresh period the governor is measuring against, ms. */
  readonly refreshMs: number;
  /**
   * Framebuffer scale worth trying next session: raised if the session never
   * had to cut, lowered if it ran out of cuts. Nothing here persists it.
   */
  readonly advisedFramebufferScale: number;
}

export interface XRQuality {
  /** True between `enter` and `exit`. */
  readonly active: boolean;
  readonly stats: XRQualityStats;

  /**
   * Call before the session starts. Sets the eye-buffer scale for the player's
   * graphics quality, which three can only apply at session creation.
   */
  prepare(renderer: WebGLRenderer, quality: RenderSettings['quality']): void;

  /**
   * Apply the tier. Call once the session is presenting.
   *
   * Returns the `RenderSettings` the renderer should be given for the session —
   * a copy, with the player's saved settings untouched. Hand it straight to
   * `RenderAPI.setSettings`; do **not** route it through the settings save, or
   * the player will find their desktop options rewritten by a headset.
   *
   * The draw-distance, fog and light-budget overrides are read by the renderer
   * when it builds a level, so they land in full on the next level and only
   * partly (scenery culling, which is per-frame) on the level in progress.
   * That is deliberate for the light budget — changing the pool size recompiles
   * every material, which inside a session is a freeze, not a hitch.
   */
  enter(renderer: WebGLRenderer, settings: RenderSettings): RenderSettings;

  /**
   * One XR frame. Called by `render/postfx.ts` from the stereo path, so a
   * session gets the governor whether or not anything else remembers to — it
   * arms itself on the first presenting frame even if `enter` was never
   * called, because foveation and the grade are safe to move on a session this
   * module knows nothing else about. Cheap: a clock read and some integer
   * arithmetic. A no-op when nothing is presenting.
   */
  tick(renderer: WebGLRenderer): void;

  /**
   * Release the tier. Returns the `RenderSettings` handed to `enter`, so the
   * caller can put the renderer back exactly as it found it. Idempotent.
   */
  exit(renderer: WebGLRenderer): RenderSettings | null;
}

class Quality implements XRQuality {
  /** Desktop exposure, held so a session can be handed back exactly as found. */
  private savedExposure: number | null = null;

  private on = false;
  /** Whether the governor is measuring. Set by `enter`, or by the first tick. */
  private governing = false;
  private saved: TuningSnapshot | null = null;
  private restore: RenderSettings | null = null;
  private rungs: readonly Rung[] = [];
  private level = 0;
  /** Down-steps taken. After the second the governor stops giving anything back. */
  private downSteps = 0;

  /** Timestamp of the previous frame, 0 when there is not one yet. */
  private last = 0;
  private refreshMs = 1000 / 72;
  private frames = 0;
  private dropped = 0;
  /** Frames and drops inside the current sample window. */
  private windowFrames = 0;
  private windowDrops = 0;
  private windowFastest = Infinity;
  private cleanWindows = 0;
  /** Windows to ignore after entering or after a step, while things settle. */
  private settle = 0;
  /** The session drops to `governorFloorHz` at most once. */
  private askedForFloorHz = false;
  /** Quality the eye-buffer scale was picked for, so the advice matches it. */
  private scaleKey: RenderSettings['quality'] = 'medium';

  get active(): boolean {
    return this.on;
  }

  get stats(): XRQualityStats {
    const scale = TUNING.render.xr.framebufferScale[this.scaleKey] ?? 1;
    return {
      level: this.level,
      frames: this.frames,
      dropped: this.dropped,
      refreshMs: this.refreshMs,
      // Only advise a change on evidence: a whole session that never had to
      // cut, or one that ran out of things to cut. Anything shorter than a
      // couple of minutes is mostly level loading and is not evidence.
      advisedFramebufferScale: this.advise(scale),
    };
  }

  private advise(scale: number): number {
    if (this.frames < 8000 || this.rungs.length === 0) return scale;
    if (this.downSteps === 0) return Math.min(1, scale + 0.1);
    if (this.level >= this.rungs.length - 1) return Math.max(0.6, scale - 0.1);
    return scale;
  }

  prepare(renderer: WebGLRenderer, quality: RenderSettings['quality']): void {
    this.scaleKey = quality;
    const scale = TUNING.render.xr.framebufferScale[quality] ?? 1;
    // Must happen before the session starts: `WebXRManager` reads the scale
    // when it builds the layer, and warns if it is changed while presenting.
    renderer.xr.setFramebufferScaleFactor(clamp(scale, 0.5, 1.5));
    // Foveation is deliberately not touched here. three defaults it to maximum
    // and `render/xr.ts` sets maximum explicitly; anything this module did
    // would only be able to make the frame more expensive.
  }

  enter(renderer: WebGLRenderer, settings: RenderSettings): RenderSettings {
    // Re-entering an active tier is a no-op that still answers with the patch,
    // so a caller that lost track cannot end up with the session half applied.
    if (this.on) return { ...settings, headBob: false };
    const t = TUNING.render.xr;
    const r = TUNING.render;

    this.saved = {
      cullDistance: r.cullDistance,
      viewDistanceCap: r.viewDistanceCap,
      fogFarScale: r.fogFarScale,
      maxDynamicLights: r.maxDynamicLights,
    };
    r.cullDistance = t.cullDistance;
    r.viewDistanceCap = t.viewDistanceCap;
    r.fogFarScale = t.fogFarScale;
    r.maxDynamicLights = t.maxDynamicLights;

    // Headsets are dimmer than monitors, and this game is deliberately near the
    // bottom of the range already — the report from a Vision Pro was simply
    // "it's really dark in there". Two causes, both fixed here rather than by
    // brightening the game for everybody: the tier cuts the dynamic light pool,
    // so fewer fixtures are real lights, and an LCD panel behind a lens loses
    // shadow detail a desktop display keeps.
    //
    // Exposure rather than ambient: lifting ambient flattens the level and
    // throws away the dark the whole game is about, while exposure moves the
    // whole curve and keeps the fixtures reading as the only light there is.
    this.savedExposure = renderer.toneMappingExposure;
    renderer.toneMappingExposure = r.toneMappingExposure * t.exposureLift;

    this.arm(renderer);
    this.scaleKey = settings.quality;
    comfortMotion = 0;
    this.on = true;
    this.restore = { ...settings };
    return {
      ...settings,
      // Head bob moves the camera without the player's head moving. On a
      // monitor it sells the walk; worn, it is the vestibular mismatch that
      // makes people take the headset off. Not negotiable in a session.
      headBob: false,
    };
  }

  /**
   * Start measuring from a clean slate, and put the ladder back on its top
   * rung. Touches only the two live knobs, never `TUNING` — so a session that
   * never called `enter` still gets a governor and nothing else.
   */
  private arm(renderer: WebGLRenderer): void {
    // Whatever the session chose for itself is the floor. `getFoveation`
    // answers undefined before a layer exists.
    this.rungs = ladder(renderer.xr.getFoveation() ?? 1);
    this.level = 0;
    this.downSteps = 0;
    this.last = 0;
    this.frames = 0;
    this.dropped = 0;
    this.windowFrames = 0;
    this.windowDrops = 0;
    this.windowFastest = Infinity;
    this.cleanWindows = 0;
    // A session's first second is level build, shader compiles and the
    // compositor settling. Judging it would cut quality for no reason.
    this.settle = 1;
    this.askedForFloorHz = false;
    gradeOn = true;

    const session = renderer.xr.getSession();
    const hz = session && typeof session.frameRate === 'number' ? session.frameRate : 0;
    if (hz > 0) this.refreshMs = 1000 / hz;

    renderer.xr.setFoveation(this.rungs[0]!.foveation);
    this.governing = true;
  }

  tick(renderer: WebGLRenderer): void {
    if (!renderer.xr.isPresenting) {
      this.governing = false;
      this.last = 0;
      return;
    }
    if (!this.governing) {
      this.arm(renderer);
      return;
    }
    const now = performance.now();
    const last = this.last;
    this.last = now;
    if (last === 0) return;

    const dtMs = now - last;
    // A gap this long is the headset off the head, a level load or a tab-out,
    // not a rendering problem.
    if (dtMs > 500) {
      this.windowFrames = 0;
      this.windowDrops = 0;
      this.windowFastest = Infinity;
      this.settle = 1;
      return;
    }

    const t = TUNING.render.xr;
    this.frames++;
    this.windowFrames++;
    if (dtMs < this.windowFastest) this.windowFastest = dtMs;
    if (dtMs > this.refreshMs * t.governorLateFactor) {
      this.dropped++;
      this.windowDrops++;
    }
    if (this.windowFrames < t.governorWindow) return;

    // No frameRate from the session: infer the refresh from the fastest frame
    // in the window. Clamped to plausible headset rates so one freak interval
    // cannot move the threshold far.
    const session = renderer.xr.getSession();
    const hz = session && typeof session.frameRate === 'number' ? session.frameRate : 0;
    this.refreshMs = hz > 0 ? 1000 / hz : clamp(this.windowFastest, 1000 / 120, 1000 / 60);

    const drops = this.windowDrops;
    const frames = this.windowFrames;
    this.windowFrames = 0;
    this.windowDrops = 0;
    this.windowFastest = Infinity;

    if (this.settle > 0) {
      this.settle--;
      return;
    }

    if (drops > frames * t.governorDropFraction) {
      this.stepDown(renderer, session);
      return;
    }

    if (drops > 0) {
      this.cleanWindows = 0;
      return;
    }
    this.cleanWindows++;
    // One step back, once. A governor that keeps trying to climb spends the
    // session oscillating, and an oscillating frame rate is worse to wear than
    // a steady lower one.
    if (this.downSteps < 2 && this.level > 0 && this.cleanWindows >= t.governorRecoverWindows) {
      this.cleanWindows = 0;
      this.settle = 1;
      this.level--;
      this.applyRung(renderer);
    }
  }

  exit(renderer: WebGLRenderer): RenderSettings | null {
    if (this.savedExposure !== null) {
      renderer.toneMappingExposure = this.savedExposure;
      this.savedExposure = null;
    }
    // Disarmed either way: a tick after this re-arms from scratch, so an early
    // or a doubled call cannot leave the governor measuring across sessions.
    this.governing = false;
    this.last = 0;
    if (!this.on) return null;
    this.on = false;

    const s = this.saved;
    if (s) {
      const r = TUNING.render;
      r.cullDistance = s.cullDistance;
      r.viewDistanceCap = s.viewDistanceCap;
      r.fogFarScale = s.fogFarScale;
      r.maxDynamicLights = s.maxDynamicLights;
    }
    this.saved = null;

    gradeOn = true;
    comfortMotion = 0;
    // Leave the next session starting from the authored tier rather than from
    // wherever this one ended up.
    renderer.xr.setFoveation(clamp01(TUNING.render.xr.foveation));

    const restore = this.restore;
    this.restore = null;
    return restore;
  }

  private stepDown(renderer: WebGLRenderer, session: XRSession | null): void {
    this.cleanWindows = 0;
    if (this.level < this.rungs.length - 1) {
      this.level++;
      this.downSteps++;
      this.settle = 1;
      this.applyRung(renderer);
      return;
    }
    // Out of quality. Ask for a rate the device can actually hold: a solid 72
    // is far better to wear than a 90 that misses one frame in ten.
    if (this.askedForFloorHz || !session) return;
    this.askedForFloorHz = true;
    const floor = TUNING.render.xr.governorFloorHz;
    const rates = session.supportedFrameRates;
    if (!rates || typeof session.updateTargetFrameRate !== 'function') return;
    let best = 0;
    for (const rate of rates) if (rate <= floor && rate > best) best = rate;
    if (best <= 0 || (session.frameRate ?? 0) <= best) return;
    // Fire and forget: a rejected request leaves the session exactly as it was.
    void session.updateTargetFrameRate(best).catch(() => {});
    this.refreshMs = 1000 / best;
    this.settle = 2;
  }

  private applyRung(renderer: WebGLRenderer): void {
    const rung = this.rungs[this.level]!;
    renderer.xr.setFoveation(rung.foveation);
    gradeOn = rung.grade;
  }
}

/**
 * The session's quality tier.
 *
 * A singleton because there is exactly one WebGL context and at most one XR
 * session, and because `render/postfx.ts` has to read the grade switch without
 * a handle on anything. Completely inert until something presents.
 */
export const xrQuality: XRQuality = new Quality();
